import React, { useEffect, useState, useContext, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { SurveyContext } from "../context/SurveyContext";
import { AuthContext } from "../context/AuthContext";
import { useInstanceStatusSocket, useSocketStatus } from "../context/SocketContext";
import api from "../services/api";
import { FaSpinner, FaExclamationTriangle, FaUsers, FaComments, FaRedo, FaWifi, FaExclamationCircle } from "react-icons/fa";
import OnboardingFlow from "./OnboardingFlow";

const InstanceLoader = ({ children }) => {
  const location = useLocation();
  const {
    instanceId,
    conditionId,
    instanceStatus,
    instanceProgress,
    isInstanceReady,
    updateInstanceStatus,
    initializationError,
    onboardingCompleted,
    conditionConfig,
    completeOnboarding,
    updateConditionConfig,
  } = useContext(SurveyContext);
  const { user } = useContext(AuthContext);
  const isAuthenticated = !!user;

  // Bypass for admin and study routes
  const isAdminRoute = location.pathname.startsWith("/admin");
  const isStudyRoute = location.pathname === "/study";

  // Bypass for auth routes (register/login) - user needs to authenticate first
  const isAuthRoute = ["/register", "/login"].includes(location.pathname);

  // Bypass for returning users going to login
  const isReturningUserLogin = location.pathname === "/login" &&
    new URLSearchParams(location.search).get("returning") === "true";

  const [polling, setPolling] = useState(false);
  const [populationStartTime] = useState(Date.now());
  const [retryCount, setRetryCount] = useState(0);
  const [showTimeout, setShowTimeout] = useState(false);
  const MAX_RETRIES = 2;
  const POPULATION_TIMEOUT_MS = 120000; // 2 minutes
  const [error, setError] = useState(null);
  const [loadingConditionConfig, setLoadingConditionConfig] = useState(false);

  // Get WebSocket connection status
  const { isConnected: socketConnected } = useSocketStatus();

  // Handle WebSocket status updates (primary method - no polling needed when connected)
  const handleSocketStatusUpdate = useCallback((status, progress, statusMessage) => {
    console.log("[InstanceLoader] WebSocket status update:", status);
    updateInstanceStatus(status, progress);

    if (status === "error") {
      setError(statusMessage || "An error occurred while preparing your session");
      setPolling(false);
    } else if (["ready", "active"].includes(status)) {
      setPolling(false);
    }
  }, [updateInstanceStatus]);

  // Subscribe to WebSocket status updates
  useInstanceStatusSocket(handleSocketStatusUpdate);

  // Poll for instance status (fallback when WebSocket not connected)
  const pollStatus = useCallback(async () => {
    if (!instanceId) return;

    try {
      const { data } = await api.get(`/instances/${instanceId}/status`);
      updateInstanceStatus(data.status, data.progress);

      if (data.status === "error") {
        setError(data.statusMessage || "An error occurred while preparing your session");
        setPolling(false);
      } else if (["ready", "active"].includes(data.status)) {
        setPolling(false);
      }
    } catch (err) {
      console.error("Error polling instance status:", err);
      // Don't stop polling on network errors, just log
    }
  }, [instanceId, updateInstanceStatus]);

  // Start polling when component mounts or instance changes (only if WebSocket not connected)
  useEffect(() => {
    if (instanceId && !isInstanceReady && !error) {
      // Always do initial poll to get current status
      pollStatus();

      // Only continue polling if WebSocket is not connected
      if (!socketConnected) {
        setPolling(true);
      }
    }
  }, [instanceId, isInstanceReady, error, socketConnected, pollStatus]);

  // Stop polling when WebSocket connects
  useEffect(() => {
    if (socketConnected && polling) {
      console.log("[InstanceLoader] WebSocket connected, stopping polling");
      setPolling(false);
    }
  }, [socketConnected, polling]);

  // Fallback polling interval (only when WebSocket not connected)
  useEffect(() => {
    if (!polling || socketConnected) return;

    // Poll every 3 seconds (slower since it's fallback)
    const interval = setInterval(pollStatus, 3000);

    return () => clearInterval(interval);
  }, [polling, socketConnected, pollStatus]);

  // Population timeout: auto-retry if stuck too long
  useEffect(() => {
    if (isInstanceReady || error || !instanceId) return;

    const check = setInterval(() => {
      const elapsed = Date.now() - populationStartTime;
      if (elapsed > POPULATION_TIMEOUT_MS && !showTimeout) {
        if (retryCount < MAX_RETRIES) {
          // Auto-retry
          setRetryCount((prev) => prev + 1);
          setShowTimeout(true);
          api.post(`/instances/${instanceId}/retry`).then(() => {
            setShowTimeout(false);
            setError(null);
          }).catch((e) => {
            console.error("Retry failed:", e);
          });
          // Reset timer by restarting the effect
        } else {
          setShowTimeout(true);
          setError("The discussion environment is taking too long to prepare. Please contact the research team for assistance.");
        }
      }
    }, 5000);

    return () => clearInterval(check);
  }, [isInstanceReady, error, instanceId, populationStartTime, retryCount, showTimeout]);

  // Fetch condition config when instance is ready (for onboarding and navbar completion redirect)
  // Always refetch if conditionId changed (even if we have a cached conditionConfig from a different condition)
  useEffect(() => {
    const fetchConditionConfig = async () => {
      if (!conditionId || loadingConditionConfig) return;

      // Check if we already have the right config cached
      if (conditionConfig && conditionConfig.id === conditionId) return;

      setLoadingConditionConfig(true);
      try {
        const { data } = await api.get(`/conditions/${conditionId}`);
        updateConditionConfig(data);
      } catch (err) {
        console.error("Error fetching condition config:", err);
      } finally {
        setLoadingConditionConfig(false);
      }
    };

    // Fetch condition config whenever instance is ready and we have a conditionId
    // This is needed both for onboarding AND for navbar's completion redirect functionality
    if (isInstanceReady && conditionId) {
      fetchConditionConfig();
    }
  }, [isInstanceReady, conditionId, conditionConfig, loadingConditionConfig, updateConditionConfig]);

  // Calculate progress percentage
  const getProgressPercentage = () => {
    if (!instanceProgress) return 0;

    const { agentsCreated, agentsTotal, postsCreated, postsTotal, commentsCreated, commentsTotal } =
      instanceProgress;

    const totalItems = (agentsTotal || 0) + (postsTotal || 0) + (commentsTotal || 0);
    if (totalItems === 0) return 0;

    const completedItems = (agentsCreated || 0) + (postsCreated || 0) + (commentsCreated || 0);
    return Math.round((completedItems / totalItems) * 100);
  };

  const handleRetry = async () => {
    setError(null);
    try {
      // Re-trigger orchestrator launch for the failed instance
      await api.post(`/instances/${instanceId}/retry`);
    } catch (err) {
      console.error("Error retrying instance:", err);
    }
    setPolling(true);
  };

  // Bypass for admin routes - no instance needed
  if (isAdminRoute) {
    return children;
  }

  // Bypass for study routes - StudyFlow manages its own lifecycle
  if (isStudyRoute) {
    return children;
  }

  // Bypass for auth routes - user needs to register/login first
  if (isAuthRoute) {
    return children;
  }

  // Bypass for returning users going to login
  if (isReturningUserLogin) {
    return children;
  }

  // If instance is ready, check onboarding status
  if (isInstanceReady) {
    // Check if conditionConfig is loaded and matches current condition
    const hasValidConditionConfig = conditionConfig && conditionConfig.id === conditionId;

    // Only show onboarding if user is authenticated and we have the right condition config
    if (!onboardingCompleted && isAuthenticated && hasValidConditionConfig) {
      return (
        <OnboardingFlow
          condition={conditionConfig}
          conditionConfig={conditionConfig}
          onComplete={completeOnboarding}
        />
      );
    }

    // If onboarding completed OR user not authenticated yet, render children
    // (children will handle redirecting unauthenticated users to login via PrivateRoute)
    if (onboardingCompleted || !isAuthenticated) {
      return children;
    }

    // Still loading condition config - show a brief loading state
    if (loadingConditionConfig || !hasValidConditionConfig) {
      return (
        <div className="min-h-screen bg-spiders-bg flex items-center justify-center">
          <div className="text-center">
            <FaSpinner className="animate-spin text-spiders-primary-400 text-3xl mx-auto mb-4" />
            <p className="text-spiders-text-secondary">Preparing your experience...</p>
          </div>
        </div>
      );
    }
  }

  // If no instance ID yet (still in condition selection), render children
  if (!instanceId) {
    return children;
  }

  // Show loading screen
  const progress = getProgressPercentage();
  const statusMessages = {
    initializing: "Initializing your session...",
    populating: "Preparing your social network experience...",
    ready: "Almost ready!",
    error: "Something went wrong",
  };

  return (
    <div className="min-h-screen bg-spiders-bg flex items-center justify-center">
      <div className="spiders-card p-8 max-w-md w-full mx-4 text-center relative overflow-hidden">
        {/* Decorative gradient */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-spiders-primary-500 via-spiders-accent-400 to-spiders-primary-500"></div>

        {/* Logo/Title */}
        <h1 className="text-3xl font-bold text-gradient-primary mb-2">Almost there!</h1>
        <p className="text-spiders-text-secondary text-sm mb-8">
          Setting up your discussion environment
        </p>

        {error || initializationError ? (
          // Error State
          <div className="mb-6">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-spiders-error/20 flex items-center justify-center">
              <FaExclamationTriangle className="text-spiders-error text-2xl" />
            </div>
            <p className="text-spiders-error mb-6">{error || initializationError}</p>
            <button
              onClick={handleRetry}
              className="spiders-btn-primary inline-flex items-center gap-2"
            >
              <FaRedo /> Try Again
            </button>
          </div>
        ) : (
          // Loading State
          <>
            {/* Status Message */}
            <p className="text-spiders-text-primary mb-6">
              {showTimeout && retryCount <= MAX_RETRIES
                ? `This is taking longer than expected. Retrying... (attempt ${retryCount}/${MAX_RETRIES})`
                : statusMessages[instanceStatus] || "Loading..."}
            </p>

            {/* Progress Bar */}
            <div className="mb-6">
              <div className="w-full bg-spiders-bg-dark rounded-full h-3 mb-2 overflow-hidden">
                <div
                  className="h-3 rounded-full transition-all duration-500 ease-out bg-gradient-to-r from-spiders-primary-500 to-spiders-accent-400"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <p className="text-spiders-text-muted text-sm">{progress}% complete</p>
            </div>

            {/* Progress Details */}
            {instanceProgress && (
              <div className="grid grid-cols-3 gap-3 mb-6">
                <div className="bg-spiders-bg-surface border border-spiders-border rounded-lg p-3">
                  <FaUsers className="text-spiders-primary-400 mx-auto mb-1" />
                  <p className="text-spiders-text-primary font-semibold text-sm">
                    {instanceProgress.agentsCreated || 0}/{instanceProgress.agentsTotal || 0}
                  </p>
                  <p className="text-spiders-text-muted text-xs">Members</p>
                </div>
                <div className="bg-spiders-bg-surface border border-spiders-border rounded-lg p-3">
                  <FaComments className="text-spiders-accent-400 mx-auto mb-1" />
                  <p className="text-spiders-text-primary font-semibold text-sm">
                    {instanceProgress.postsCreated || 0}/{instanceProgress.postsTotal || 0}
                  </p>
                  <p className="text-spiders-text-muted text-xs">Posts</p>
                </div>
                <div className="bg-spiders-bg-surface border border-spiders-border rounded-lg p-3">
                  <FaComments className="text-spiders-success mx-auto mb-1" />
                  <p className="text-spiders-text-primary font-semibold text-sm">
                    {instanceProgress.commentsCreated || 0}/{instanceProgress.commentsTotal || 0}
                  </p>
                  <p className="text-spiders-text-muted text-xs">Replies</p>
                </div>
              </div>
            )}

            {/* Spinner */}
            <div className="w-12 h-12 mx-auto mb-6 rounded-full bg-spiders-primary-950 flex items-center justify-center">
              <FaSpinner className="animate-spin text-spiders-primary-400 text-xl" />
            </div>

            {/* Info Text */}
            <p className="text-spiders-text-muted text-sm">
              We're creating a personalized discussion environment for you.
              <br />
              This usually takes about 30-60 seconds.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default InstanceLoader;
